/**
 * Enhanced Retirement Planning Engine
 * Full fintech-grade retirement calculations with:
 * - Accumulation phase (year-by-year account growth)
 * - Drawdown phase (multiple withdrawal strategies)
 * - State pension / Social Security
 * - RMDs and UK tax-free lump sum
 * - Funding ratio, income replacement ratio, sustainable withdrawal rate
 * - Monte Carlo simulations with sequence-of-return risk
 */

import { runMonteCarloSimulation } from '../portfolio/monteCarlo.js';
import { calculateAnnualContribution } from '../accountUtils.js';
import { getInvestmentMarketValue } from '../netWorthCalculations.js';

/**
 * Calculate annual contribution from account settings
 * ✓ UNIFIED CONTRIBUTION TRACKING: Now uses shared utility function
 * @deprecated Use calculateAnnualContribution from accountUtils.js directly
 */
export function getAnnualContribution(account) {
  return calculateAnnualContribution(account);
}

/**
 * Determine if an account can be used for retirement planning
 * Now includes ALL investable accounts: retirement, crypto, and taxable
 */
export function isRetirementAccount(account) {
  const retirementTypes = [
    '401k', 'roth_ira', 'traditional_ira', 'sep_ira', 'simple_ira',
    'pension', 'sipp', 'isa', 'stocks_shares_isa', // UK accounts
    '403b', '457', 'thrift_savings_plan' // Other US retirement
  ];

  // Include accounts that are:
  // 1. Traditional retirement account types
  // 2. Specifically marked as retirement accounts
  // 3. Crypto accounts (can be used for retirement)
  // 4. Taxable brokerage accounts (can be used for retirement)
  // Exclude only: pure savings accounts unless specifically marked
  return retirementTypes.includes(account.type?.toLowerCase()) ||
         account.accountPurpose === 'retirement' ||
         account.accountPurpose === 'crypto' ||
         account.accountPurpose === 'taxable';
}

/**
 * Calculate Required Minimum Distribution (US)
 * Based on IRS Uniform Lifetime Table
 */
export function calculateRMD(age, accountBalance, country = 'US') {
  if (country !== 'US') return 0;
  if (age < 73) return 0; // RMD age as of 2024 (SECURE 2.0 Act)

  // Simplified RMD calculation using distribution period
  const distributionPeriods = {
    73: 26.5, 74: 25.5, 75: 24.6, 76: 23.7, 77: 22.9,
    78: 22.0, 79: 21.1, 80: 20.2, 81: 19.4, 82: 18.5,
    83: 17.7, 84: 16.8, 85: 16.0, 86: 15.2, 87: 14.4,
    88: 13.7, 89: 12.9, 90: 12.2, 91: 11.5, 92: 10.8,
    93: 10.1, 94: 9.5, 95: 8.9
  };

  const period = distributionPeriods[age] || (age >= 96 ? 8.4 : 26.5);
  return accountBalance / period;
}

/**
 * Calculate UK pension tax-free lump sum (25% of pot)
 * Supports both lump sum and phased withdrawals
 * Maximum cap: £268,275 (Lump Sum Allowance as of 2024/25)
 */
export function calculateUKTaxFreeLumpSum(pensionBalance, hasBeenTaken = false, options = {}) {
  if (hasBeenTaken) return 0;

  const {
    takeAsLumpSum = true,
    phasedYears = 5,
    yearNumber = 0
  } = options;

  // Maximum Lump Sum Allowance (LSA) for UK pensions
  const UK_LUMP_SUM_ALLOWANCE = 268275;

  // Calculate 25% of pension balance, capped at the LSA
  const totalTaxFree = Math.min(pensionBalance * 0.25, UK_LUMP_SUM_ALLOWANCE);

  if (takeAsLumpSum) {
    // Traditional: take all 25% at once (capped at LSA)
    return totalTaxFree;
  } else {
    // Phased: spread over specified years
    // Can take up to 25% of each withdrawal tax-free (total capped at LSA)
    return totalTaxFree / phasedYears;
  }
}

/**
 * Calculate net rental income from properties
 * @param {Array} rentalProperties - Array of rental property configurations
 * @param {number} year - Year in retirement (for inflation adjustments)
 * @param {number} inflationRate - Annual inflation rate
 */
export function calculateRentalIncome(rentalProperties = [], year = 0, inflationRate = 0.025) {
  if (!rentalProperties || rentalProperties.length === 0) return 0;

  let totalNetIncome = 0;

  rentalProperties.forEach(property => {
    const {
      monthlyRent = 0,
      annualExpenses = 0, // maintenance, insurance, property tax
      vacancyRate = 0.05, // default 5% vacancy
      startYear = 0,
      endYear = 30,
      inflationAdjusted = true
    } = property;

    // Check if property is active in this year
    if (year < startYear || year > endYear) return;

    // Calculate gross annual rent
    let annualRent = monthlyRent * 12;
    let expenses = annualExpenses;

    // Apply inflation if enabled
    if (inflationAdjusted && year > 0) {
      const inflationMultiplier = Math.pow(1 + inflationRate, year);
      annualRent *= inflationMultiplier;
      expenses *= inflationMultiplier;
    }

    // Account for vacancy
    const effectiveRent = annualRent * (1 - vacancyRate);

    // Net income = effective rent - expenses
    const netIncome = Math.max(0, effectiveRent - expenses);
    totalNetIncome += netIncome;
  });

  return totalNetIncome;
}

/**
 * Calculate part-time work income during retirement
 * @param {Object} partTimeWork - Part-time work configuration
 * @param {number} age - Current age
 * @param {number} year - Year in retirement
 * @param {number} inflationRate - Annual inflation rate
 */
export function calculatePartTimeIncome(partTimeWork = null, age = 65, year = 0, inflationRate = 0.025) {
  if (!partTimeWork) return 0;

  const {
    annualAmount = 0,
    startAge = 65,
    endAge = 70,
    inflationAdjusted = true
  } = partTimeWork;

  // Check if currently working part-time
  if (age < startAge || age > endAge) return 0;

  let income = annualAmount;

  // Apply inflation if enabled
  if (inflationAdjusted && year > 0) {
    income *= Math.pow(1 + inflationRate, year);
  }

  return income;
}

/**
 * Calculate annuity income
 * @param {Array} annuities - Array of annuity configurations
 * @param {number} age - Current age
 * @param {number} year - Year in retirement
 */
export function calculateAnnuityIncome(annuities = [], age = 65, year = 0) {
  if (!annuities || annuities.length === 0) return 0;

  let totalAnnuityIncome = 0;

  annuities.forEach(annuity => {
    const {
      annualPayment = 0,
      startAge = 65,
      endAge = 95, // lifetime or specific end age
      type = 'fixed', // 'fixed' or 'escalating'
      escalationRate = 0.025, // if escalating
      jointLife = false
    } = annuity;

    // Check if annuity has started
    if (age < startAge || age > endAge) return;

    let payment = annualPayment;

    // Apply escalation if escalating type
    if (type === 'escalating' && year > 0) {
      const yearsFromStart = age - startAge;
      payment *= Math.pow(1 + escalationRate, yearsFromStart);
    }

    totalAnnuityIncome += payment;
  });

  return totalAnnuityIncome;
}

/**
 * Calculate other passive income streams
 * @param {Array} otherIncomes - Array of other income configurations
 * @param {number} age - Current age
 * @param {number} year - Year in retirement
 * @param {number} inflationRate - Annual inflation rate
 */
export function calculateOtherIncome(otherIncomes = [], age = 65, year = 0, inflationRate = 0.025) {
  if (!otherIncomes || otherIncomes.length === 0) return 0;

  let totalOtherIncome = 0;

  otherIncomes.forEach(income => {
    const {
      description = '',
      annualAmount = 0,
      startAge = 65,
      endAge = 95,
      inflationAdjusted = true
    } = income;

    // Check if income is active
    if (age < startAge || age > endAge) return;

    let amount = annualAmount;

    // Apply inflation if enabled
    if (inflationAdjusted && year > 0) {
      amount *= Math.pow(1 + inflationRate, year);
    }

    totalOtherIncome += amount;
  });

  return totalOtherIncome;
}

/**
 * Calculate spouse/partner income
 * @param {Object} spouseIncome - Spouse income configuration
 * @param {number} primaryAgeInRetirement - Primary person's current age in retirement (retirementAge + year)
 * @param {number} year - Year in retirement
 * @param {number} inflationRate - Annual inflation rate
 * @param {number} primaryCurrentAge - Primary person's current age (optional, for proper spouse age calculation)
 * @param {number} primaryRetirementAge - Primary person's retirement age (optional, for proper spouse age calculation)
 * @param {boolean} includeSpouseStatePension - Whether to include spouse's state pension (optional, defaults to true)
 */
export function calculateSpouseIncome(spouseIncome = null, primaryAgeInRetirement = 65, year = 0, inflationRate = 0.025, primaryCurrentAge = null, primaryRetirementAge = null, includeSpouseStatePension = true) {
  if (!spouseIncome || !spouseIncome.included) return 0;

  const {
    statePension = 0,
    partTimeWork = 0,
    partTimeStartAge = 65,
    partTimeEndAge = 70,
    spouseAge = 65, // This is spouse's CURRENT age
    otherIncome = 0,
    inflationAdjusted = true
  } = spouseIncome;

  // Calculate spouse's actual age at this point in time
  let currentSpouseAge;
  if (primaryCurrentAge !== null && primaryRetirementAge !== null) {
    // Correct calculation: account for years until primary retires, then add years in retirement
    const yearsUntilPrimaryRetirement = primaryRetirementAge - primaryCurrentAge;
    const spouseAgeAtPrimaryRetirement = spouseAge + yearsUntilPrimaryRetirement;
    currentSpouseAge = spouseAgeAtPrimaryRetirement + year;
  } else {
    // Fallback: assume spouseAge is at primary's retirement (old behavior for compatibility)
    currentSpouseAge = spouseAge + year;
  }

  let totalSpouseIncome = 0;

  // State pension (starts at state pension age) - Only if toggle is enabled
  if (includeSpouseStatePension) {
    const spouseStatePensionAge = spouseIncome.statePensionAge || 67;
    if (currentSpouseAge >= spouseStatePensionAge) {
      totalSpouseIncome += statePension;
    }
  }

  // Part-time work
  if (currentSpouseAge >= partTimeStartAge && currentSpouseAge <= partTimeEndAge) {
    let workIncome = partTimeWork;
    if (inflationAdjusted && year > 0) {
      workIncome *= Math.pow(1 + inflationRate, year);
    }
    totalSpouseIncome += workIncome;
  }

  // Other income
  if (otherIncome > 0) {
    let other = otherIncome;
    if (inflationAdjusted && year > 0) {
      other *= Math.pow(1 + inflationRate, year);
    }
    totalSpouseIncome += other;
  }

  return totalSpouseIncome;
}

/**
 * Estimate state pension / Social Security benefits
 */
export function estimateStatePension(params) {
  const { country, currentAge, retirementAge, fullRetirementAge = 67, annualEarnings = 0 } = params;

  if (country === 'UK') {
    // UK State Pension (2024/25): £11,502.40 per year for full new state pension
    const fullPension = 11502.40;

    // Return estimated pension amount regardless of retirement age
    // The age-based logic (when to actually receive it) is handled in simulateDrawdown
    return fullPension; // Simplified - assumes full contributions
  }

  if (country === 'US') {
    // Simplified Social Security estimate
    // Full retirement age benefit is ~40% of average indexed monthly earnings
    const monthlyEarnings = annualEarnings / 12;
    const primaryInsuranceAmount = monthlyEarnings * 0.4; // Simplified

    // Reduction for early claiming, increase for delayed claiming
    let factor = 1.0;
    if (retirementAge < fullRetirementAge) {
      const monthsEarly = (fullRetirementAge - retirementAge) * 12;
      factor = 1 - (monthsEarly * 0.00556); // ~6.67% per year early
    } else if (retirementAge > fullRetirementAge) {
      const monthsLate = (retirementAge - fullRetirementAge) * 12;
      factor = 1 + (monthsLate * 0.00667); // 8% per year delayed (up to age 70)
    }

    return Math.max(0, primaryInsuranceAmount * factor * 12); // Annual amount
  }

  return 0;
}

/**
 * Accumulation Phase: Simulate account growth from current age to retirement
 * ⭐ LIVE PRICING: Now supports priceMap for accurate current market values
 */
export function simulateAccumulation(params) {
  const {
    accounts = [],
    investments = [],
    currentAge,
    retirementAge,
    expectedReturn = 0.07, // Default 7% real return
    volatility = 0.15, // Default 15% volatility
    inflationRate = 0.025,
    riskProfile = 'moderate',
    priceMap = null // ⭐ LIVE PRICING: Optional price map from pricing service
  } = params;

  // Adjust returns based on risk profile
  const riskAdjustments = {
    conservative: { return: -0.02, volatility: -0.05 },
    moderate: { return: 0, volatility: 0 },
    aggressive: { return: 0.015, volatility: 0.05 }
  };

  const adjustment = riskAdjustments[riskProfile] || riskAdjustments.moderate;
  const adjustedReturn = expectedReturn + adjustment.return;
  const adjustedVolatility = Math.max(0.01, volatility + adjustment.volatility);

  const yearsToRetirement = retirementAge - currentAge;
  const projections = [];

  // Calculate starting balances for each account using LIVE market values
  const accountBalances = {};
  accounts.forEach(account => {
    const accountInvestments = investments.filter(inv => inv.accountId === account.id);
    // ⭐ LIVE PRICING: Use getInvestmentMarketValue with priceMap for accurate values
    accountBalances[account.id] = accountInvestments.reduce((sum, inv) => sum + getInvestmentMarketValue(inv, priceMap), 0);
  });

  // Simulate year by year
  for (let year = 0; year <= yearsToRetirement; year++) {
    const age = currentAge + year;
    const yearData = {
      year,
      age,
      accounts: {},
      totalBalance: 0
    };

    accounts.forEach(account => {
      const currentBalance = accountBalances[account.id] || 0;
      const annualContribution = getAnnualContribution(account);

      // Apply returns (simplified - assumes contributions at year start)
      const totalInvested = currentBalance + annualContribution;
      const growth = totalInvested * adjustedReturn;
      const newBalance = totalInvested + growth;

      accountBalances[account.id] = newBalance;
      yearData.accounts[account.id] = {
        name: account.name,
        type: account.type,
        balance: newBalance,
        contribution: annualContribution,
        growth,
        isRetirementAccount: isRetirementAccount(account)
      };

      yearData.totalBalance += newBalance;
    });

    projections.push(yearData);
  }

  return {
    projections,
    totalAtRetirement: projections[projections.length - 1]?.totalBalance || 0,
    totalContributions: projections[projections.length - 1]?.year *
      accounts.reduce((sum, acc) => sum + getAnnualContribution(acc), 0),
    yearsToRetirement
  };
}

/**
 * Drawdown Phase: Simulate retirement withdrawals with multiple strategies
 * Now includes: rental income, part-time work, annuities, other passive income, spouse income
 */
export function simulateDrawdown(params) {
  const {
    accountsAtRetirement = {},
    currentAge, // NEW: Primary person's current age (for spouse age calculation)
    retirementAge,
    lifeExpectancy = 95,
    desiredAnnualIncome,
    essentialSpending = desiredAnnualIncome * 0.7, // 70% essential by default
    discretionarySpending = desiredAnnualIncome * 0.3, // 30% discretionary
    oneOffExpenses = [], // Array of { year, amount, description }
    withdrawalStrategy = 'inflationAdjusted',
    inflationRate = 0.025,
    expectedReturn = 0.05, // Lower return in retirement (more conservative)
    volatility = 0.12,
    statePension = 0,
    statePensionAge = 67,
    country = 'US',
    taxFreeLumpSumTaken = false,
    // NEW: Additional income sources
    rentalProperties = [], // Array of rental property configurations
    partTimeWork = null, // Part-time work during retirement
    annuities = [], // Array of annuity income
    otherIncome = [], // Other passive income streams
    spouseIncome = null, // Spouse/partner income
    ukTaxFreeLumpSumOptions = { takeAsLumpSum: true, phasedYears: 5 }, // UK-specific lump sum options
    includeSpouseStatePension = true // NEW: Toggle for spouse state pension
  } = params;

  const yearsInRetirement = lifeExpectancy - retirementAge;
  const projections = [];

  // Extract balance values from account objects
  // accountsAtRetirement now has structure: { id: { balance, type, name } }
  let accountBalances = {};
  Object.keys(accountsAtRetirement).forEach(accountId => {
    accountBalances[accountId] = accountsAtRetirement[accountId].balance;
  });

  let ukTaxFreeLumpSumAvailable = !taxFreeLumpSumTaken;
  let ukTaxFreeLumpSumYearsTaken = 0;

  for (let year = 0; year <= yearsInRetirement; year++) {
    const age = retirementAge + year;
    const yearData = {
      year,
      age,
      accounts: {},
      totalBalance: 0,
      withdrawal: 0,
      statePension: 0,
      rmd: 0,
      taxFreeLumpSum: 0,
      oneOffExpense: 0,
      totalIncome: 0,
      // NEW: Additional income tracking
      rentalIncome: 0,
      partTimeIncome: 0,
      annuityIncome: 0,
      otherPassiveIncome: 0,
      spouseIncome: 0,
      spouseStatePension: 0, // NEW: Separate tracking for spouse's state pension
      expenses: {
        essential: essentialSpending * Math.pow(1 + inflationRate, year),
        discretionary: discretionarySpending * Math.pow(1 + inflationRate, year)
      }
    };

    // Calculate state pension if eligible
    if (age >= statePensionAge) {
      yearData.statePension = statePension;
    }

    // NEW: Calculate rental income
    yearData.rentalIncome = calculateRentalIncome(rentalProperties, year, inflationRate);

    // NEW: Calculate part-time work income
    yearData.partTimeIncome = calculatePartTimeIncome(partTimeWork, age, year, inflationRate);

    // NEW: Calculate annuity income
    yearData.annuityIncome = calculateAnnuityIncome(annuities, age, year);

    // NEW: Calculate other passive income
    yearData.otherPassiveIncome = calculateOtherIncome(otherIncome, age, year, inflationRate);

    // NEW: Calculate spouse income (total including state pension)
    const totalSpouseIncome = calculateSpouseIncome(spouseIncome, age, year, inflationRate, currentAge, retirementAge, includeSpouseStatePension);

    // NEW: Calculate spouse state pension separately for chart display
    if (spouseIncome && spouseIncome.included && includeSpouseStatePension) {
      const spouseStatePension = spouseIncome.statePension || 0;
      const spouseStatePensionAge = spouseIncome.statePensionAge || 67;
      const spouseAge = spouseIncome.spouseAge || retirementAge;

      // Calculate spouse's age at this point
      let currentSpouseAge;
      if (currentAge !== null && retirementAge !== null) {
        const yearsUntilPrimaryRetirement = retirementAge - currentAge;
        const spouseAgeAtPrimaryRetirement = spouseAge + yearsUntilPrimaryRetirement;
        currentSpouseAge = spouseAgeAtPrimaryRetirement + year;
      } else {
        currentSpouseAge = spouseAge + year;
      }

      // Store spouse state pension separately if eligible
      if (currentSpouseAge >= spouseStatePensionAge) {
        yearData.spouseStatePension = spouseStatePension;
      }
    }

    yearData.spouseIncome = totalSpouseIncome;

    // Check for one-off expenses
    const oneOff = oneOffExpenses.find(e => e.year === year);
    if (oneOff) {
      yearData.oneOffExpense = oneOff.amount;
      yearData.oneOffDescription = oneOff.description;
    }

    // Calculate total income from all non-portfolio sources
    const totalNonPortfolioIncome = yearData.statePension +
                                    yearData.rentalIncome +
                                    yearData.partTimeIncome +
                                    yearData.annuityIncome +
                                    yearData.otherPassiveIncome +
                                    yearData.spouseIncome;

    // Calculate total needed (expenses + one-off - all other income)
    const totalExpenses = yearData.expenses.essential + yearData.expenses.discretionary + yearData.oneOffExpense;
    const neededFromPortfolio = Math.max(0, totalExpenses - totalNonPortfolioIncome);

    // Calculate withdrawal based on strategy
    const totalPortfolioValue = Object.values(accountBalances).reduce((sum, bal) => sum + bal, 0);
    yearData.withdrawal = calculateWithdrawal({
      strategy: withdrawalStrategy,
      year,
      totalPortfolioValue,
      desiredAmount: neededFromPortfolio,
      initialAmount: desiredAnnualIncome,
      inflationRate,
      essentialSpending: yearData.expenses.essential,
      discretionarySpending: yearData.expenses.discretionary
    });

    // UK tax-free lump sum (can take all at once or phased over multiple years)
    if (country === 'UK' && ukTaxFreeLumpSumAvailable) {
      // Helper function to check if account is a pension type
      const isPensionAccount = (accountId) => {
        const accountType = accountsAtRetirement[accountId]?.type?.toLowerCase() || '';
        return accountType.includes('pension') || accountType === 'sipp';
      };

      const pensionBalance = Object.entries(accountBalances)
        .filter(([id, _]) => isPensionAccount(id))
        .reduce((sum, [_, bal]) => sum + bal, 0);

      const { takeAsLumpSum, phasedYears } = ukTaxFreeLumpSumOptions;

      if (takeAsLumpSum && year === 0) {
        // Take all 25% at retirement (ADDITIONAL income in year 0)
        yearData.taxFreeLumpSum = calculateUKTaxFreeLumpSum(pensionBalance, false, { takeAsLumpSum: true });
        yearData.isLumpSumAdditionalIncome = true; // Flag for totalIncome calculation
        ukTaxFreeLumpSumAvailable = false;
      } else if (!takeAsLumpSum && ukTaxFreeLumpSumYearsTaken < phasedYears) {
        // Phased withdrawals: Tax-free portion is PART OF the withdrawal, not additional
        // Track it for display purposes but DON'T add to total income
        yearData.taxFreeLumpSum = calculateUKTaxFreeLumpSum(
          pensionBalance,
          false,
          { takeAsLumpSum: false, phasedYears, yearNumber: ukTaxFreeLumpSumYearsTaken }
        );
        yearData.isLumpSumAdditionalIncome = false; // Flag: DON'T add to totalIncome
        ukTaxFreeLumpSumYearsTaken++;

        if (ukTaxFreeLumpSumYearsTaken >= phasedYears) {
          ukTaxFreeLumpSumAvailable = false;
        }
      }

      // CRITICAL: Actually withdraw the lump sum from pension accounts
      if (yearData.taxFreeLumpSum > 0) {
        const pensionAccounts = Object.entries(accountBalances)
          .filter(([id, _]) => isPensionAccount(id));

        if (pensionAccounts.length > 0) {
          const totalPensionBalance = pensionAccounts.reduce((sum, [_, bal]) => sum + bal, 0);

          // Track lump sum withdrawals per account
          yearData.lumpSumWithdrawals = {};

          // Proportionally withdraw from each pension account
          pensionAccounts.forEach(([accountId, balance]) => {
            const proportion = balance / totalPensionBalance;
            const withdrawalAmount = yearData.taxFreeLumpSum * proportion;
            accountBalances[accountId] -= withdrawalAmount;
            yearData.lumpSumWithdrawals[accountId] = withdrawalAmount;
          });
        }
      }
    }

    // Calculate RMDs for US accounts
    if (country === 'US' && age >= 73) {
      Object.entries(accountBalances).forEach(([accountId, balance]) => {
        const account = accountsAtRetirement[accountId];
        if (account?.type === 'traditional_ira' || account?.type === '401k') {
          const rmd = calculateRMD(age, balance, country);
          yearData.rmd += rmd;
        }
      });
    }

    // Ensure we withdraw at least the RMD
    const actualWithdrawal = Math.max(yearData.withdrawal, yearData.rmd);

    // Execute withdrawals using tax-efficient waterfall
    const { updatedBalances, withdrawalDetails } = executeWithdrawals({
      accountBalances,
      accountsAtRetirement,
      withdrawalAmount: actualWithdrawal,
      country,
      age
    });

    accountBalances = updatedBalances;

    // Merge lump sum withdrawals into withdrawal details for tracking
    if (yearData.lumpSumWithdrawals) {
      Object.entries(yearData.lumpSumWithdrawals).forEach(([accountId, amount]) => {
        withdrawalDetails[accountId] = (withdrawalDetails[accountId] || 0) + amount;
      });
    }

    // Apply investment returns to remaining balances
    Object.keys(accountBalances).forEach(accountId => {
      const growth = accountBalances[accountId] * expectedReturn;
      accountBalances[accountId] += growth;

      yearData.accounts[accountId] = {
        name: accountsAtRetirement[accountId]?.name,
        type: accountsAtRetirement[accountId]?.type,
        balance: accountBalances[accountId],
        withdrawal: withdrawalDetails[accountId] || 0,
        growth
      };

      yearData.totalBalance += accountBalances[accountId];
    });

    // Calculate total income from ALL sources
    // IMPORTANT: For phased UK tax-free lump sums, the tax-free portion is PART OF the withdrawal
    // Only add taxFreeLumpSum if it's additional income (lump sum option), not if it's phased
    const taxFreeLumpSumToAdd = yearData.isLumpSumAdditionalIncome ? yearData.taxFreeLumpSum : 0;

    yearData.totalIncome = actualWithdrawal +
                          yearData.statePension +
                          taxFreeLumpSumToAdd +
                          yearData.rentalIncome +
                          yearData.partTimeIncome +
                          yearData.annuityIncome +
                          yearData.otherPassiveIncome +
                          yearData.spouseIncome;
    yearData.actualWithdrawal = actualWithdrawal;

    projections.push(yearData);

    // Stop if portfolio is depleted
    if (yearData.totalBalance <= 0) {
      break;
    }
  }

  return {
    projections,
    depletionAge: projections[projections.length - 1]?.age,
    success: projections[projections.length - 1]?.age >= lifeExpectancy,
    finalBalance: projections[projections.length - 1]?.totalBalance || 0
  };
}

/**
 * Calculate withdrawal amount based on strategy
 */
function calculateWithdrawal(params) {
  const {
    strategy,
    year,
    totalPortfolioValue,
    desiredAmount,
    initialAmount,
    inflationRate,
    essentialSpending,
    discretionarySpending
  } = params;

  if (totalPortfolioValue <= 0) return 0;

  // Calculate inflation-adjusted initial withdrawal for reference
  const inflationAdjustedInitial = initialAmount * Math.pow(1 + inflationRate, year);

  switch (strategy) {
    case 'fixed':
      // Fixed dollar amount: Withdraw the same dollar amount every year (not inflation-adjusted)
      // This means real purchasing power decreases over time
      return Math.min(initialAmount, totalPortfolioValue);

    case 'percentage':
      // Fixed percentage (4% rule): Always withdraw 4% of current portfolio value
      // This naturally adjusts to portfolio performance but can be volatile
      return totalPortfolioValue * 0.04;

    case 'inflationAdjusted':
      // Inflation-adjusted: Initial amount adjusted for inflation each year
      // Maintains purchasing power but doesn't respond to portfolio changes
      return Math.min(inflationAdjustedInitial, totalPortfolioValue);

    case 'guardrails':
      // Guardrails strategy (Guyton-Klinger)
      // Dynamically adjust withdrawal based on portfolio performance
      // Uses inflation-adjusted amount as baseline
      const currentRate = inflationAdjustedInitial / totalPortfolioValue;

      // If withdrawal rate exceeds 6%, cut back to 5% (portfolio struggling)
      if (currentRate > 0.06) {
        return totalPortfolioValue * 0.05;
      }
      // If withdrawal rate below 3%, can increase by 10% (portfolio thriving)
      if (currentRate < 0.03 && totalPortfolioValue > inflationAdjustedInitial * 25) {
        return Math.min(inflationAdjustedInitial * 1.1, totalPortfolioValue * 0.05);
      }
      // Otherwise use inflation-adjusted amount
      return Math.min(inflationAdjustedInitial, totalPortfolioValue);

    case 'essentialOnly':
      // Only withdraw essential spending (safety mode)
      return essentialSpending;

    case 'dynamic':
      // Dynamic withdrawal based on portfolio health
      // Withdraws more when portfolio is healthy, less when struggling
      const yearsOfExpensesRemaining = totalPortfolioValue / inflationAdjustedInitial;

      // If portfolio can sustain 25+ years, use 5% rate
      // If 15-25 years, use 4% rate
      // If less than 15 years, use 3.5% rate (conservative)
      let sustainableRate;
      if (yearsOfExpensesRemaining > 25) {
        sustainableRate = 0.05;
      } else if (yearsOfExpensesRemaining > 15) {
        sustainableRate = 0.04;
      } else {
        sustainableRate = 0.035;
      }

      return totalPortfolioValue * sustainableRate;

    default:
      // Fallback to inflation-adjusted
      return Math.min(inflationAdjustedInitial, totalPortfolioValue);
  }
}

/**
 * Execute withdrawals using tax-efficient waterfall
 */
function executeWithdrawals(params) {
  const {
    accountBalances,
    accountsAtRetirement,
    withdrawalAmount,
    country,
    age
  } = params;

  const updatedBalances = { ...accountBalances };
  const withdrawalDetails = {};
  let remaining = withdrawalAmount;

  // Tax-efficient withdrawal order:
  // 1. Taxable accounts (brokerage, ISA in UK)
  // 2. Tax-deferred accounts (Traditional IRA, 401k, SIPP, Pension)
  // 3. Tax-free accounts (Roth IRA, HSA for medical)

  const accountsByTaxType = {
    taxable: [],
    taxDeferred: [],
    taxFree: []
  };

  Object.keys(updatedBalances).forEach(accountId => {
    const account = accountsAtRetirement[accountId];
    if (!account) return;

    const taxDeferredTypes = ['traditional_ira', '401k', '403b', 'sep_ira', 'simple_ira', 'pension', 'sipp'];
    const taxFreeTypes = ['roth_ira', 'hsa'];

    if (taxFreeTypes.includes(account.type)) {
      accountsByTaxType.taxFree.push(accountId);
    } else if (taxDeferredTypes.includes(account.type)) {
      accountsByTaxType.taxDeferred.push(accountId);
    } else {
      accountsByTaxType.taxable.push(accountId);
    }
  });

  // Withdraw in tax-efficient order
  const withdrawalOrder = [
    ...accountsByTaxType.taxable,
    ...accountsByTaxType.taxDeferred,
    ...accountsByTaxType.taxFree
  ];

  for (const accountId of withdrawalOrder) {
    if (remaining <= 0) break;

    const available = updatedBalances[accountId];
    const toWithdraw = Math.min(remaining, available);

    updatedBalances[accountId] -= toWithdraw;
    withdrawalDetails[accountId] = toWithdraw;
    remaining -= toWithdraw;
  }

  return { updatedBalances, withdrawalDetails };
}

/**
 * Calculate key retirement metrics
 */
export function calculateRetirementMetrics(params) {
  const {
    totalAtRetirement,
    desiredAnnualIncome,
    currentAnnualIncome = desiredAnnualIncome,
    statePension = 0,
    lifeExpectancy = 95,
    retirementAge
  } = params;

  const yearsInRetirement = lifeExpectancy - retirementAge;

  // Income replacement ratio
  const incomeReplacementRatio = (desiredAnnualIncome + statePension) / currentAnnualIncome;

  // Sustainable withdrawal rate (simplified)
  const sustainableWithdrawalRate = totalAtRetirement > 0 ?
    (desiredAnnualIncome - statePension) / totalAtRetirement : 0;

  // Funding ratio (is portfolio sufficient?)
  // Simple calculation: need 25x annual expenses (4% rule)
  const requiredPortfolio = (desiredAnnualIncome - statePension) * 25;
  const fundingRatio = requiredPortfolio > 0 ? totalAtRetirement / requiredPortfolio : 0;

  // Years of coverage
  const yearsCovered = sustainableWithdrawalRate > 0 ?
    1 / sustainableWithdrawalRate : 0;

  return {
    fundingRatio: Math.round(fundingRatio * 100) / 100,
    incomeReplacementRatio: Math.round(incomeReplacementRatio * 100) / 100,
    sustainableWithdrawalRate: Math.round(sustainableWithdrawalRate * 1000) / 10, // As percentage
    requiredPortfolio,
    shortfall: Math.max(0, requiredPortfolio - totalAtRetirement),
    surplus: Math.max(0, totalAtRetirement - requiredPortfolio),
    yearsCovered: Math.round(yearsCovered * 10) / 10,
    isOnTrack: fundingRatio >= 1.0
  };
}

/**
 * Run Monte Carlo simulation for retirement
 * Now includes all new income sources
 */
export function runRetirementMonteCarlo(params) {
  const {
    accountsAtRetirement,
    currentAge, // NEW: Primary person's current age (for spouse age calculation)
    retirementAge,
    lifeExpectancy = 95,
    desiredAnnualIncome,
    essentialSpending,
    discretionarySpending,
    withdrawalStrategy = 'inflationAdjusted',
    inflationRate = 0.025,
    expectedReturn = 0.05,
    volatility = 0.12,
    statePension = 0,
    statePensionAge = 67,
    country = 'US',
    numScenarios = 1000,
    oneOffExpenses = [],
    // NEW: Additional income sources
    rentalProperties = [],
    partTimeWork = null,
    annuities = [],
    otherIncome = [],
    spouseIncome = null,
    includeSpouseStatePension = true // NEW: Toggle for spouse state pension
  } = params;

  const scenarios = [];
  const yearsInRetirement = lifeExpectancy - retirementAge;

  for (let i = 0; i < numScenarios; i++) {
    const result = simulateDrawdownWithRandomReturns({
      accountsAtRetirement,
      currentAge,
      retirementAge,
      lifeExpectancy,
      desiredAnnualIncome,
      essentialSpending,
      discretionarySpending,
      withdrawalStrategy,
      inflationRate,
      expectedReturn,
      volatility,
      statePension,
      statePensionAge,
      country,
      oneOffExpenses,
      seed: i,
      // NEW: Pass additional income sources
      rentalProperties,
      partTimeWork,
      annuities,
      otherIncome,
      spouseIncome,
      includeSpouseStatePension
    });

    scenarios.push(result);
  }

  // Calculate percentiles
  const percentiles = calculatePercentiles(scenarios, yearsInRetirement);

  // Calculate success rate
  const successCount = scenarios.filter(s => s.success).length;
  const successRate = successCount / numScenarios;

  // Calculate median depletion age
  const depletionAges = scenarios
    .filter(s => !s.success)
    .map(s => s.depletionAge)
    .sort((a, b) => a - b);
  const medianDepletionAge = depletionAges.length > 0 ?
    depletionAges[Math.floor(depletionAges.length / 2)] : lifeExpectancy;

  return {
    scenarios,
    percentiles,
    successRate: Math.round(successRate * 100),
    medianDepletionAge,
    numScenarios
  };
}

/**
 * Simulate drawdown with random returns (for Monte Carlo)
 * Now includes all new income sources
 */
function simulateDrawdownWithRandomReturns(params) {
  const {
    accountsAtRetirement,
    currentAge, // NEW: Primary person's current age (for spouse age calculation)
    retirementAge,
    lifeExpectancy,
    desiredAnnualIncome,
    essentialSpending,
    discretionarySpending,
    withdrawalStrategy,
    inflationRate,
    expectedReturn,
    volatility,
    statePension,
    statePensionAge,
    country,
    oneOffExpenses,
    seed,
    // NEW: Additional income sources
    rentalProperties = [],
    partTimeWork = null,
    annuities = [],
    otherIncome = [],
    spouseIncome = null,
    includeSpouseStatePension = true // NEW: Toggle for spouse state pension
  } = params;

  const yearsInRetirement = lifeExpectancy - retirementAge;
  const projections = [];

  // Extract balance values from account objects
  let accountBalances = {};
  Object.keys(accountsAtRetirement).forEach(accountId => {
    accountBalances[accountId] = accountsAtRetirement[accountId].balance;
  });

  // Simple random number generator (seeded)
  let randomSeed = seed;
  const random = () => {
    randomSeed = (randomSeed * 9301 + 49297) % 233280;
    return randomSeed / 233280;
  };

  // Box-Muller transform for normal distribution
  const normalRandom = () => {
    const u1 = random();
    const u2 = random();
    return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  };

  for (let year = 0; year <= yearsInRetirement; year++) {
    const age = retirementAge + year;

    // Calculate state pension
    const statePensionIncome = age >= statePensionAge ? statePension : 0;

    // NEW: Calculate all additional income sources
    const rentalIncome = calculateRentalIncome(rentalProperties, year, inflationRate);
    const partTimeIncome = calculatePartTimeIncome(partTimeWork, age, year, inflationRate);
    const annuityIncome = calculateAnnuityIncome(annuities, age, year);
    const otherPassiveIncome = calculateOtherIncome(otherIncome, age, year, inflationRate);
    const spouseIncomeAmount = calculateSpouseIncome(spouseIncome, age, year, inflationRate, currentAge, retirementAge, includeSpouseStatePension);

    const totalNonPortfolioIncome = statePensionIncome +
                                    rentalIncome +
                                    partTimeIncome +
                                    annuityIncome +
                                    otherPassiveIncome +
                                    spouseIncomeAmount;

    // Check for one-off expenses
    const oneOff = oneOffExpenses.find(e => e.year === year);
    const oneOffExpense = oneOff ? oneOff.amount : 0;

    // Calculate expenses (inflation-adjusted)
    const essential = essentialSpending * Math.pow(1 + inflationRate, year);
    const discretionary = discretionarySpending * Math.pow(1 + inflationRate, year);
    const totalExpenses = essential + discretionary + oneOffExpense;
    const neededFromPortfolio = Math.max(0, totalExpenses - totalNonPortfolioIncome);

    // Calculate withdrawal
    const totalPortfolioValue = Object.values(accountBalances).reduce((sum, bal) => sum + bal, 0);
    const withdrawal = calculateWithdrawal({
      strategy: withdrawalStrategy,
      year,
      totalPortfolioValue,
      desiredAmount: neededFromPortfolio,
      initialAmount: desiredAnnualIncome,
      inflationRate,
      essentialSpending: essential,
      discretionarySpending: discretionary
    });

    // Execute withdrawals
    const { updatedBalances } = executeWithdrawals({
      accountBalances,
      accountsAtRetirement,
      withdrawalAmount: withdrawal,
      country,
      age
    });

    accountBalances = updatedBalances;

    // Apply random returns to remaining balances
    const annualReturn = expectedReturn + (volatility * normalRandom());
    const cappedReturn = Math.min(Math.max(annualReturn, -0.5), 1.0); // Cap at -50% to +100%

    Object.keys(accountBalances).forEach(accountId => {
      const growth = accountBalances[accountId] * cappedReturn;
      accountBalances[accountId] += growth;
    });

    const totalBalance = Object.values(accountBalances).reduce((sum, bal) => sum + bal, 0);

    projections.push({
      year,
      age,
      totalBalance,
      withdrawal,
      statePension: statePensionIncome
    });

    if (totalBalance <= 0) {
      break;
    }
  }

  return {
    projections,
    depletionAge: projections[projections.length - 1]?.age,
    success: projections[projections.length - 1]?.age >= lifeExpectancy,
    finalBalance: projections[projections.length - 1]?.totalBalance || 0
  };
}

/**
 * Calculate percentiles from Monte Carlo scenarios
 */
function calculatePercentiles(scenarios, yearsInRetirement) {
  const percentileData = [];

  for (let year = 0; year <= yearsInRetirement; year++) {
    const balancesThisYear = scenarios
      .filter(s => s.projections[year])
      .map(s => s.projections[year].totalBalance)
      .sort((a, b) => a - b);

    if (balancesThisYear.length === 0) break;

    percentileData.push({
      year,
      age: scenarios[0].projections[year]?.age,
      p10: balancesThisYear[Math.floor(balancesThisYear.length * 0.1)],
      p25: balancesThisYear[Math.floor(balancesThisYear.length * 0.25)],
      p50: balancesThisYear[Math.floor(balancesThisYear.length * 0.5)],
      p75: balancesThisYear[Math.floor(balancesThisYear.length * 0.75)],
      p90: balancesThisYear[Math.floor(balancesThisYear.length * 0.9)]
    });
  }

  return percentileData;
}

/**
 * Compare different withdrawal strategies
 */
export function compareWithdrawalStrategies(params) {
  const strategies = [
    'fixed',
    'percentage',
    'inflationAdjusted',
    'guardrails',
    'dynamic'
  ];

  const results = {};

  strategies.forEach(strategy => {
    const result = simulateDrawdown({
      ...params,
      withdrawalStrategy: strategy
    });

    results[strategy] = {
      ...result,
      avgAnnualWithdrawal: result.projections.reduce((sum, p) => sum + p.actualWithdrawal, 0) / result.projections.length,
      volatility: calculateWithdrawalVolatility(result.projections)
    };
  });

  return results;
}

/**
 * Calculate volatility of withdrawals
 */
function calculateWithdrawalVolatility(projections) {
  const withdrawals = projections.map(p => p.actualWithdrawal);
  const mean = withdrawals.reduce((sum, w) => sum + w, 0) / withdrawals.length;
  const variance = withdrawals.reduce((sum, w) => sum + Math.pow(w - mean, 2), 0) / withdrawals.length;
  return Math.sqrt(variance);
}

export default {
  getAnnualContribution,
  isRetirementAccount,
  calculateRMD,
  calculateUKTaxFreeLumpSum,
  estimateStatePension,
  simulateAccumulation,
  simulateDrawdown,
  calculateRetirementMetrics,
  runRetirementMonteCarlo,
  compareWithdrawalStrategies,
  // NEW: Additional income calculation functions
  calculateRentalIncome,
  calculatePartTimeIncome,
  calculateAnnuityIncome,
  calculateOtherIncome,
  calculateSpouseIncome
};
